import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { InputAdornment, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import { useFormik } from "formik";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import CompassIcon from "../../assets/CompassIconPlain.png";
import "./Login.css";
import MfaSetupModal from "./MfaSetupModal";
import MfaVerifyModal from "./MfaVerifyModal";
import { WEB_PUSH_LOGIN_PROMPT_KEY } from "../../components/WebPushPermissionBanner";
import { DEFAULT_HOME } from "../../utils/defaultHome";
import {
  SESSION_END_MESSAGES,
  consumeSessionEndReason,
} from "../../config/sessionTimeout";

require("dotenv").config();
const BASE_URL = process.env.REACT_APP_BASE_URL
  ? process.env.REACT_APP_BASE_URL.replace("v1", "v2")
  : "http://localhost:5002/api/v2";

// Mirrors cortex-be clientLoginPlatformPolicy denial copy (GMA-1308).
const CLIENT_LOGIN_PLATFORM_FORBIDDEN_MESSAGE =
  "Client accounts must sign in with the MyCompass app. Web and CompassCare Mobile sign-in is not available for clients.";

async function parseLoginResponse(response) {
  try {
    return await response.json();
  } catch (_err) {
    return null;
  }
}

const loginSchema = yup.object({
  email: yup
    .string("Enter your email.")
    .email("Enter a valid email.")
    .required("Email is required."),
  password: yup
    .string("Enter your password.")
    .required("Password is required."),
});

function LoginPage() {
  // GMA-1909, widened by issue #672: explain the sign-out exactly once,
  // whichever sign-out it was. Consumed in an effect so render stays
  // side-effect free; the answer itself is memoised per page load, because
  // StrictMode runs both the initializer and this effect twice and the second
  // caller must get the same answer as the first.
  //
  // The reason drives the COPY; an unrecognised reason resolves to no message
  // and therefore no notice, rather than an empty red box.
  const [sessionEndReason, setSessionEndReason] = useState(null);
  useEffect(() => {
    setSessionEndReason(consumeSessionEndReason());
  }, []);
  const sessionEndMessage = sessionEndReason
    ? SESSION_END_MESSAGES[sessionEndReason]
    : null;

  const [showMfaVerifyModal, setShowMfaVerifyModal] = useState(false);
  const [showMfaSetupModal, setShowMfaSetupModal] = useState(false);
  const [accessToken, setAccessToken] = useState();
  const [session, setSession] = useState();
  // GMA-1650: carry the factor details from loginNew into the verify modal so it
  // can render the chooser / email-code / authenticator variants correctly.
  const [mfaType, setMfaType] = useState();
  const [mfaSelectionRequired, setMfaSelectionRequired] = useState(false);
  const [mfaOptions, setMfaOptions] = useState([]);
  const [mfaDestination, setMfaDestination] = useState();
  useEffect(() => {
    setShowMfaVerifyModal(false);
    setShowMfaSetupModal(false);
    setAccessToken(undefined);
    setSession(undefined);
    setMfaType(undefined);
    setMfaSelectionRequired(false);
    setMfaOptions([]);
    setMfaDestination(undefined);
  }, []);

  const [showPassword, setShowPassword] = useState(false);
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      try {
        const mfaEnabled =
          process.env.REACT_APP_FEATURE_FLAG_MFA_ENABLED === "true";
        // Always use loginNew - the old login endpoint has JWT parsing issues
        const response = await fetch(`${BASE_URL}/users/loginNew`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-App-Platform": "web_browser",
          },
          body: JSON.stringify(values),
        });

        const resp = await parseLoginResponse(response);
        if (!resp) {
          toast.error("Login failed. Please try again.");
          return;
        }
        if (!response.ok || !resp.success) {
          const errorMsg =
            resp.code === "CLIENT_LOGIN_PLATFORM_FORBIDDEN"
              ? resp.message || CLIENT_LOGIN_PLATFORM_FORBIDDEN_MESSAGE
              : resp.message || resp.error || "Login failed. Please try again.";
          toast.error(errorMsg);
          return;
        }

        if (!mfaEnabled) {
          if (resp.mfaRequired || resp.mfaSetupRequired) {
            toast.error("MFA is required but not configured on this account. Please contact support.");
            return;
          }
          handleMfaVerifySuccess(resp);
          return;
        }

        if (resp.mfaSetupRequired) {
          setAccessToken(resp.token);
          setShowMfaSetupModal(true);
        } else if (resp.mfaRequired) {
          setSession(resp.session);
          setMfaType(resp.mfaType);
          setMfaSelectionRequired(Boolean(resp.mfaSelectionRequired));
          setMfaOptions(resp.mfaOptions || []);
          setMfaDestination(resp.destination);
          setShowMfaVerifyModal(true);
        } else {
          handleMfaVerifySuccess(resp);
        }
      } catch (error) {
        // Log only a safe summary, never the raw error object: this handler
        // POSTs the user's email + password, and a raw-object console log on an
        // auth path can ship those credentials to the console / Sentry breadcrumbs.
        console.error("Login failed:", error?.response?.status ?? error.message);
        toast.error("Error login: " + error.message);
      }
    },
  });

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleMfaVerifyClose = () => {
    setShowMfaVerifyModal(false);
  };

  const handleMfaSetupClose = () => {
    setShowMfaSetupModal(false);
  };

  const handleMfaVerifySuccess = (data) => {
    setShowMfaVerifyModal(false);
    const jwtToken = data.access_token;
    const refreshToken = data.refresh_token;

    if (!jwtToken || typeof jwtToken !== "string") {
      toast.error("Login failed: invalid server response. Please try again.");
      return;
    }

    localStorage.setItem("token", jwtToken);
    localStorage.setItem("refresh_token", refreshToken);
    localStorage.setItem("user_email", formik.values.email);
    const jwtData = jwtDecode(jwtToken);
    const userData = jwtData.user;

    if (userData.isCaregiver || userData.isContact) {
      toast.success(data.message);
    } else {
      toast.info("User not allowed");
      localStorage.clear();
      return;
    }

    if (
      userData.isCaregiver ||
      userData.isAdmin ||
      userData.isSuperAdmin
    ) {
      try {
        sessionStorage.setItem(WEB_PUSH_LOGIN_PROMPT_KEY, "1");
      } catch (_err) {
        // ignore
      }
    }

    setTimeout(() => {
      window.location.href = userData.isTermsAccepted
        ? DEFAULT_HOME
        : "/accept-terms";
    }, 2000);
  };

  return (
    <div className="App-content-center App-content-bg-custom">
      <MfaVerifyModal
        isOpen={showMfaVerifyModal}
        onClose={handleMfaVerifyClose}
        session={session}
        email={formik.values.email}
        onSuccess={handleMfaVerifySuccess}
        mfaType={mfaType}
        mfaSelectionRequired={mfaSelectionRequired}
        mfaOptions={mfaOptions}
        destination={mfaDestination}
      />
      <MfaSetupModal
        isOpen={showMfaSetupModal}
        onClose={handleMfaSetupClose}
        email={formik.values.email}
        accessToken={accessToken}
      />
      {/* GMA-1909: .App-content-center is a flex ROW, so a notice added as a
          bare sibling of the login container lays out BESIDE the form. The
          stack makes the two a column. */}
      <div className="loginPageStack">
      {sessionEndMessage && (
        <div className="session-idle-notice" role="status" data-testid="idle-logout-notice">
          {sessionEndMessage}
        </div>
      )}
      <div className="loginContainerUnique">
        <div className="loginLogoUnique">
          <img src={CompassIcon} alt="Compass Care by Gray Matters Alliance" />
          <h1 style={{ marginBottom: "24px", marginTop: "24px" }}>
            Welcome to Compass Care
          </h1>
          <p style={{ fontSize: "1.2rem" }}>Enter your info to get started.</p>
          <p style={{ fontSize: "0.9rem" }}>
            Use Google Chrome for this application as other browsers may not
            work as effectively.
          </p>
        </div>
        <div>
          <Box
            component="form"
            sx={{ "& > :not(style)": { m: 1, width: "25ch" } }}
            noValidate
            onSubmit={formik.handleSubmit}
          >
            <TextField
              id="email"
              name="email"
              label="Email"
              type="email"
              autoComplete="username"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              style={{ width: "100%" }}
              autoFocus
            />

            <TextField
              id="password"
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              required
              style={{ width: "100%" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </InputAdornment>
                ),
              }}
            />
            <div className="forgotContainer">
              <a className="forgotLink" href="/users/forgot-password">
                Forgot Password?
              </a>
            </div>
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="btn-primary loginButtonUnique"
              style={{ width: "100%" }}
            >
              {formik.isSubmitting ? "Signing in..." : "Sign in"}
            </button>
          </Box>
        </div>
      </div>
      </div>
    </div>
  );
}

export default LoginPage;
